import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const cavalaireSurMer: ChauffeurPage = {
  slug: "cavalaire-sur-mer",
  title: "Chauffeur privé & VTC à Cavalaire-sur-Mer",
  subtitle:
    "Chauffeur privé à Cavalaire-sur-Mer : grande plage, port de plaisance, plongée et transferts vers les aéroports de Toulon-Hyères et de Nice.",
  keywords: [
    "chauffeur privé cavalaire-sur-mer",
    "vtc cavalaire",
    "transfert aéroport cavalaire",
    "chauffeur cavalaire port",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Cavalaire-sur-Mer",
    subtitle:
      "La station balnéaire à la porte du massif des Maures : transferts aéroport, mise à disposition et longue distance, 7j/7.",
    keywords: [
      "VTC Cavalaire",
      "chauffeur port de Cavalaire",
      "transfert aéroport Toulon-Hyères",
      "chauffeur golfe de Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Cavalaire-sur-Mer",
    badges: [
      "Aéroport Toulon-Hyères ~45 min",
      "Suivi de vol",
      "Chauffeurs FR/EN",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Cavalaire-sur-Mer",
    items: [
      {
        title: "Deux aéroports desservis",
        description:
          "Toulon-Hyères, le plus proche à environ 45 min, et Nice Côte d'Azur à près de 1h45 : nous choisissons selon votre vol.",
      },
      {
        title: "Porte du massif des Maures",
        description:
          "Les routes sinueuses vers Le Rayol, La Croix-Valmer et le golfe de Saint-Tropez sont maîtrisées par le chauffeur.",
      },
      {
        title: "Port & grande plage",
        description:
          "Dépose au port de plaisance, sur la promenade et aux clubs de plongée, sans quête de stationnement l'été.",
      },
      {
        title: "Service noté 5,0/5",
        description:
          "Basés sur la Côte d'Azur, ponctuels et discrets, nous affichons 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Cavalaire",
    services: [
      {
        title: "Transfert aéroport de Toulon-Hyères",
        text: "La liaison la plus courte, environ 45 min, avec suivi de vol depuis et vers l'aéroport de Toulon-Hyères.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Trajet longue distance vers Nice Côte d'Azur, près de 1h45 par l'autoroute, en berline ou van premium.",
      },
      {
        title: "Gare TGV de Toulon",
        text: "Navette vers la gare de Toulon pour les TGV Paris et les correspondances, départ calé sur votre horaire.",
      },
      {
        title: "Mise à disposition",
        text: "Un chauffeur dédié à l'heure ou à la journée pour le golfe, les plages et les villages des Maures.",
      },
      {
        title: "Longue distance & Saint-Tropez",
        text: "Vers Saint-Tropez, Cannes, Monaco et l'Italie, ou desserte depuis Cannes et Nice jusqu'à Cavalaire.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Port de Cavalaire",
        description: "Port de plaisance et quais du centre-ville.",
      },
      {
        name: "Grande plage",
        description: "Longue plage de sable et sa promenade du bord de mer.",
      },
      {
        name: "Cap Lardier & Cap Taillat",
        description: "Sites de plongée et sentiers du littoral préservé.",
      },
      {
        name: "La Croix-Valmer & Le Rayol",
        description: "Communes voisines et corniche des Maures.",
      },
      {
        name: "Aéroport de Toulon-Hyères",
        description: "L'aéroport le plus proche, à environ 45 min.",
      },
      {
        name: "Golfe de Saint-Tropez",
        description: "Saint-Tropez et Ramatuelle, à une trentaine de minutes.",
      },
    ],
  },
  generic: {
    heading: "Cavalaire-sur-Mer, entre golfe et massif des Maures",
    subheading:
      "Une station balnéaire à l'écart des grands axes, où un chauffeur fait vraiment gagner du temps.",
    paragraphs: [
      "Cavalaire-sur-Mer ferme le golfe de Saint-Tropez au sud et ouvre le massif des Maures : de belles routes, mais sinueuses, et une affluence estivale qui sature vite les abords du port et de la grande plage. Un chauffeur privé vous dépose au plus près de votre adresse et vous épargne la recherche d'un stationnement rare de juin à septembre.",
      "Pour l'arrivée, deux aéroports encadrent la station : Toulon-Hyères, le plus proche à une quarantaine de minutes, et Nice Côte d'Azur, à près de 1h45 par l'A57. Selon votre vol, nous calons l'heure de départ et suivons l'appareil pour un accueil sans attente ; la gare TGV de Toulon reste l'option la plus directe vers Paris.",
      "Sur place, la mise à disposition garde le même chauffeur pour vos allers-retours plages, vos dîners dans le golfe ou vos escapades sur la corniche des Maures, avec des forfaits fixes définis à l'avance et un devis immédiat.",
    ],
    bullets: [
      "Dépose au plus près de l'adresse",
      "Deux aéroports au choix",
      "Forfaits fixes & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver un chauffeur à Cavalaire",
    steps: [
      "Indiquez votre vol (Toulon-Hyères ou Nice) ou votre adresse à Cavalaire.",
      "Nous recommandons le véhicule selon la distance, les bagages et le groupe.",
      "Le chauffeur suit le vol et cale le départ pour fluidifier l'arrivée.",
      "Transfert direct ou mise à disposition pour les plages et le golfe.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Cavalaire-sur-Mer"),
    {
      q: "Quel est l'aéroport le plus proche de Cavalaire ?",
      a: "L'aéroport de Toulon-Hyères est le plus proche, à environ 45 minutes. L'aéroport de Nice Côte d'Azur, plus grand, se situe à près de 1h45 par l'autoroute.",
    },
    {
      q: "Assurez-vous les transferts vers la gare TGV de Toulon ?",
      a: "Oui. La gare de Toulon dessert les TGV vers Paris et les correspondances ; nous calons le départ sur votre horaire de train, avec une marge pour le trafic estival du littoral.",
    },
    {
      q: "Combien de temps à l'avance faut-il réserver ?",
      a: "Réservez au plus tôt de juin à septembre, période très demandée sur le golfe de Saint-Tropez ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle internationale des plages et des villas du golfe.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E ou Classe S, van premium 7 places pour les groupes et les bagages, SUV et minibus VIP selon l'occasion.",
    },
    {
      q: "Desservez-vous Saint-Tropez et les plages voisines ?",
      a: "Oui, Saint-Tropez et Ramatuelle sont à une trentaine de minutes ; nous assurons aussi Le Rayol, La Croix-Valmer et les clubs de plongée, en aller simple ou en mise à disposition.",
    },
    {
      q: "Faites-vous les longues distances depuis Cavalaire ?",
      a: "Oui, trajets toute distance : Cannes, Nice, Monaco, Marseille et l'Italie, ainsi que la desserte depuis Cannes ou Nice vers Cavalaire, en véhicule confortable et chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur privé Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    {
      label: "Chauffeur privé Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Chauffeur privé à Cannes", href: "/chauffeur/cannes" },
    {
      label: "Voitures à louer à Cavalaire-sur-Mer",
      href: "/location/location-voiture-cavalaire-sur-mer",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Cavalaire-sur-Mer",
    text: "Transfert aéroport, gare de Toulon ou journée dans le golfe : réservez votre chauffeur, réponse rapide 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Cavalaire — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Cavalaire-sur-Mer : transferts aéroports de Toulon-Hyères (~45 min) et de Nice, gare TGV de Toulon, plages et golfe de Saint-Tropez.",
    canonical: "/chauffeur/cavalaire-sur-mer",
  },
};

export default cavalaireSurMer;
